import { ref, watch } from 'vue';

// Opt-in for now: light unless the user picked the dark scheme, no system preference detection.
export type ColorScheme = 'light' | 'dark';

const colorSchemeStorageKey = 'kgrColorScheme';

// The styleguide derives the dark colours with relative color syntax (iOS 16.4+); older WebViews stay light.
export const darkThemeSupported = typeof CSS !== 'undefined' && CSS.supports('color', 'oklch(from red l c h)');

function readItem(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // without storage the choice still applies to the current page view
    }
}

function readColorScheme(): ColorScheme {
    return readItem(colorSchemeStorageKey) === 'dark' ? 'dark' : 'light';
}

const colorScheme = ref<ColorScheme>(readColorScheme());

let initialized = false;

/** Applies the stored choice to <html>; call before mounting the app so the first paint is right. */
export function initAppearance(): void {
    if (initialized) {
        return;
    }
    initialized = true;

    watch(
        colorScheme,
        (scheme) => {
            if (darkThemeSupported && scheme === 'dark') {
                document.documentElement.setAttribute('data-kgr-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-kgr-theme');
            }
            writeItem(colorSchemeStorageKey, scheme);
        },
        { immediate: true },
    );
}

export function useAppearance() {
    return { colorScheme, darkThemeSupported };
}
